// Varselkø (operatørens «innboks»): samler HVT/TST, fiendtlig nærhet, hurtigmål,
// bevegelses-/samband-avvik og OSINT-nøkkelord i ÉN prioritert liste (kritisk →
// info). Motoren er @skade/contracts buildAlertFeed; dette er kun DOM-visningen.
// Ren DOM (all felt-tekst via textContent). Docked flytende panel i GNÅ-stil.
import { type Alert, type AlertSeverity } from '@skade/contracts';

const SEV_COLOR: Record<AlertSeverity, string> = {
  kritisk: '#ff3b3b', advarsel: '#ff9a3b', info: '#8fb0c8',
};
const SEV_LABEL: Record<AlertSeverity, string> = {
  kritisk: 'KRITISK', advarsel: 'ADVARSEL', info: 'INFO',
};

export interface AlertsHooks {
  getAlerts: () => Alert[];
  /** Klikk på et varsel med refId → fokuser kilden på kartet om mulig. */
  onFocus?: (a: Alert) => void;
  /**
   * Kvitter et varsel som krever det (SENS-33). Utelates → kvitter-knappen
   * bygges ikke (samme mønster som `allCall` i kanaloversikten).
   */
  onKvitter?: (a: Alert) => void;
}

export class AlertsPanel {
  readonly el: HTMLElement;
  private open = false;
  private listEl!: HTMLElement;

  constructor(private hooks: AlertsHooks) {
    this.el = this.build();
    this.render();
  }

  toggle() { this.open = !this.open; this.el.classList.toggle('open', this.open); if (this.open) this.render(); }

  private build(): HTMLElement {
    const p = div('report-composer alerts-panel');
    const head = div('report-head');
    head.append(span('report-title', 'Varselkø'));
    const x = document.createElement('button');
    x.className = 'report-x'; x.textContent = '×'; x.onclick = () => this.toggle();
    head.append(x);
    p.append(head);
    this.listEl = div('alerts-list');
    p.append(this.listEl);
    return p;
  }

  render() {
    const alerts = this.hooks.getAlerts();
    this.listEl.replaceChildren();
    if (!alerts.length) {
      this.listEl.append(span('alerts-empty', 'Ingen aktive varsler'));
      return;
    }
    for (const a of alerts) {
      const row = div('alert-row');
      const dot = span('alert-dot', '');
      dot.style.background = SEV_COLOR[a.severity];
      row.append(dot);
      const body = div('alert-body');
      const top = div('alert-top');
      top.append(span('alert-sev', SEV_LABEL[a.severity]), span('alert-title', a.title));
      body.append(top, span('alert-detail', a.detail));
      // ⚠ KVITTERINGSKRAV FØR FOKUS-KLIKKET (SENS-33). Et varsel som er lest opp
      // og krever kvittering skal ikke kunne forsvinne i lista bare fordi
      // operatøren klikket på det for å se kilden — kvittering er en EGEN,
      // uttalt handling, ikke en bivirkning av å se på varselet.
      if (a.krevKvittering) body.append(this.kvitterRad(a));
      row.append(body);
      if (a.refId && this.hooks.onFocus) {
        row.classList.add('clickable');
        row.onclick = () => this.hooks.onFocus!(a);
      }
      this.listEl.append(row);
    }
  }

  /** Kvitteringsraden for et varsel som krever det (SENS-33). */
  private kvitterRad(a: Alert): HTMLElement {
    const rad = div('alert-kvittering');
    if (a.kvittertMs !== undefined) {
      rad.append(span('alert-kvittert', `✓ Kvittert${a.kvittertAv ? ` av ${a.kvittertAv}` : ''}`));
      return rad;
    }
    rad.append(span('alert-kvittering-krav', '⚠ Kvittering kreves'));
    const knapp = document.createElement('button');
    knapp.type = 'button';
    knapp.className = 'alert-kvitter-btn';
    knapp.textContent = 'Kvitter';
    knapp.disabled = !this.hooks.onKvitter;
    knapp.onclick = (e) => {
      e.stopPropagation();
      this.hooks.onKvitter?.(a);
    };
    rad.append(knapp);
    return rad;
  }
}

function div(cls: string): HTMLElement { const e = document.createElement('div'); e.className = cls; return e; }
function span(cls: string, text: string): HTMLElement { const e = document.createElement('span'); e.className = cls; e.textContent = text; return e; }
